// Baker 2: the structure DRAW PLAN (APP_BAKER §3 "One collider") — which batch
// every structures.bin triangle joins, how it is shaded, which chunk it is
// drawn in, and what a chunk costs. PURE module (no THREE, no DOM) shared by
// the draw (client/src/structs2.js) and the budget gate
// (scripts/check-focus.mjs, the check_budgets twin), so the gate counts the
// triangles and draw calls the client really issues.
//
// Everything is decided by TABLE ROWS keyed by the artifact's own enums
// (game/structures.js KINDS / MATS / TF, append-only): a new kind or material
// is a ROW here, never a code path. A kind or material index with no row FAILS
// FAST (kindRow / matRow throw with the index) — the draw never skips a
// triangle silently; scripts/check-focus.mjs asserts every KINDS / MATS entry
// has a row, so a new S6.d kind is red at the gate before it reaches a client.
import { KINDS, MATS, TF, SRC, railingsOf } from '../../game/structures.js';
import { railingChains, railingGeom } from './railing.js';
import * as WD from '../../game/waysdraw.js';
import WAYSLOOK from '../../pipeline/wayslook.json' with { type: 'json' };
import STRUCTTEX from '../materials/struct/manifest.json' with { type: 'json' };
export { STRUCTTEX };
// (S6.d B4c-5 P0d) a namespace import: the fork kernels (forkRails, legWalk,
// railNet, runFrames, railPoint) are read at call time, so a waysdraw.js
// without them breaks only a deck turnout (railRuns fails fast there), never
// the module graph the client loads
const renderOf = (e) => WD.renderOf(e);

// ------------------------------------------------------------------ batches
// Per chunk AT MOST two draws (§3.7: +2–4 per visible chunk):
//   lit  — daylight surfaces: flat-normal Lambert (scene lights, fog), a flat
//          per-material colour times a box-mapped detail texture; STAIR
//          triangles get tread nosings in the same draw (shader, no discard)
//   bore — surfaces under ground: the bore look:
//          boreTexture x THE BORE LIGHT (boreLight: portal daylight + the
//          ceiling-lamp pools, coloured by tunnel type) as vertex colour,
//          unlit, FOG-FREE (white fog inside a dark bore reads as glow),
//          per-material tint; + (D1-TUNC) the bore rails, the ceiling
//          luminaires and the near bore sleepers (flat uv: the texture's mean)
//   grate  — (WAYS-G A3) open steel grating: the lit program ALPHA-TESTED
//          (discard, never blended) by a procedural pattern — the sleepers
//          across a funicular-track strip, the open treads of a
//          funicular-stair sheet at the walkway riser — faded to solid
//          where the pattern falls under ~2 px (no shimmer at distance);
//          only grating triangles (MAT_DRAW grating batch), so the discard
//          never costs the lit draw its early depth test
export const BATCHES = ['lit', 'bore', 'grate'];

// ---------------------------------------------------------------- far rule
// FAR_RULE (the far draw matched to the near draw; the ring is
// gone, the rule stays). Beyond the near nodes the drawn ground G is the
// ground tree's coarser nodes (an error-bounded TIN of L0, geomorphed:
// groundtree.js groundDrawn = terrain.groundDrawn), which sits ABOVE or
// BELOW the true L0 surface T0 by up to the node's tolerance — feet float,
// buried faces show, or a foot sinks into a crest.
// The rule: every GROUND-CONTACT vertex (its gap g = T0 - y within
// [-far_skin_m, far_deep_m]: on the skin or buried) of a kind whose row says
// far: 'drop' is MOVED by w * (T0 - G), SIGNED (lowered where the drawn
// ground is low, lifted where it is high) — its exposure over the drawn
// ground equals its exposure over L0 in both directions (check-feel).
// Above the skin the shift fades to 0 at far_fade_m over T0 (farWeight),
// so a near-ground face moves as a whole; a vertex sharing its position with
// a far:'ribbon' record stays (farContacts, the seam rule).
// T0 per vertex is a BAKE artifact (structures ground, STG1 — pipeline/bake2.py
// _structures_ground; absent -> no rule, the static draw).
//   drop   — follows the drawn ground (feet, toes, buried faces)
//   ribbon — static: the kind carries or lies under a ways2 ribbon, which is
//            static at its true height (untouchable; the ribbon's own far
//            float is S10's) — dropping it would part it from its lane
export const FAR = ['drop', 'ribbon'];

// kind -> batch. ONE ROW PER KINDS ENTRY (fail fast on a missing one).
export const KIND_DRAW = {
  other: { batch: 'lit', far: 'drop', about: 'unclassified structure: daylight Lambert like any exposed solid; far: ground contacts follow the drawn far ring (FAR_RULE)' },
  'bore-lining': { batch: 'bore', far: 'drop', about: 'bore walls, crown and floor slab sit in rock: no sun reaches them — lamp-shaded boreTexture, fog-free (fixes the S6.a "lining lighting unoccluded" item: the flat Lambert lit the tube interior by the sun); far: the mouth ring follows the far ring with its portal; deeper lining is skipped (far_deep_m)' },
  portal: { batch: 'lit', far: 'drop', about: 'headwall / wing walls face the open cut: daylight; a lining-material face on a portal still goes to the bore batch (MAT_DRAW lining) — a mouth shell\'s cover ceiling; its side walls carry the retaining walls\' material (emit.flight.shell_side_wall, S6.d B4c-5 P0c: A SHELL\'S SIDE IS A WALL), daylight like any retaining wall; far: headwall / wing-wall feet follow the far ring — the measured L2 float of a portal foot is 2.4 m p95' },
  deck: { batch: 'lit', far: 'ribbon', about: 'bridge deck top: daylight; rails on it come from ways.json (railRuns); far: STATIC — the deck carries the ways2 ribbon, which is static at its true height (untouchable, S10); a dropped deck end would part from its own lane' },
  girder: { batch: 'lit', far: 'ribbon', about: 'girder / soffit: daylight; far: static with the deck it carries' },
  pier: { batch: 'lit', far: 'drop', about: 'piers stand in air: daylight; far: pier feet follow the far ring' },
  abutment: { batch: 'lit', far: 'drop', about: 'abutment blocks: their exposed faces are daylight, the buried rest is hidden by the skin; far: abutment feet and buried faces follow the far ring — the worst measured kind (L2 buried p95 2.8 m, grade 3.1 m)' },
  parapet: { batch: 'lit', far: 'ribbon', thin: true, about: 'parapets / railings: daylight; far: static with the deck / wall top it rides; THIN (S6.d 2b rule D): drawn within DRAW.thin_view_m only — a deck / fill parapet is at most 1.1 m tall, a coping 0.3 m, both 0.25 m thick; (bridge railings, owner 2026-10-16) an OPEN railing\'s quad (collide-only) draws as posts + rails (client/src/railing.js, DRAW.railing) in the thin tail' },
  underpass: { batch: 'bore', far: 'drop', about: 'underpass walls, ceiling and floor are covered by the crossing structure: lamp-shaded bore look (baker 1 drew underpasses with tunnelRun too); far: mouth feet follow the far ring' },
  'retaining-wall': { batch: 'lit', far: 'drop', about: 'retaining walls face the open corridor: daylight; far: wall feet follow the far ring' },
  embankment: { batch: 'lit', far: 'drop', body: true, about: 'embankment wall / fill face: daylight; far: fill toe follows the far ring; BODY (farBodies): past structures.view_m the whole fill follows it' },
  'cut-slope': { batch: 'lit', far: 'drop', body: true, about: 'cut slope faces: daylight; far: cut toe follows the far ring; BODY (farBodies): past structures.view_m the whole cut follows it' },
  gallery: { batch: 'lit', far: 'drop', about: 'avalanche gallery OUTER shell: the roof and the valley side are daylight; since S6.d M4 every shell triangle is one-sided outward and NO_COLLIDE (drawn only), its inner twin is kind gallery-lining; far: gallery feet follow the far ring (L1 grade p95 6.4 m)' },
  stair: { batch: 'lit', far: 'ribbon', about: 'raised flights: daylight; STAIR triangles draw tread nosings (DRAW.riser_m); far: static — the stairs ribbon (ways2) lies on the flight' },
  junction: { batch: 'lit', far: 'ribbon', about: 'junction surfaces: daylight; far: static — a junction surface is under ribbons' },
  apron: { batch: 'lit', far: 'drop', about: 'portal aprons lie outside the mouth: daylight; far: follows the far ring WITH the mouth it meets — as ribbon it opened 216 of the 222 seams at the mouths (underpass 80, bore-lining 70, gallery 66, check-feel "no seam opened"); its outer end meets a ways2 lane that stays static (S10, STATE M5)' },
  fixture: { batch: 'lit', far: 'drop', about: 'generic kit fixture: daylight; far: fixture feet follow the far ring' },
  'gallery-lining': { batch: 'bore', far: 'drop', about: 'avalanche gallery INNER face (S6.d M4, kind 17): the twin of every gallery shell triangle, one-sided toward the interior and collidable (the pair collides once) — covered by the roof: lamp-shaded bore look like an underpass; far: follows the far ring with its outer shell (gallery)' },
  'skin-copy': { batch: 'lit', far: 'drop', felt_only: true, about: 'THE FELT SKIN PAST THE UNION IS A RECORD OF THE DRAWN SKIN (S6.d B4c-5 P-1c, kind 19, world/emit.js skinPastUnion): a collide-only copy of the drawn terrain skin\'s quads beside an open mouth, outside the union of the bores\' cut volumes, at the skin the game feels (the bake\'s quantized grid, bilinear) — no new height. felt_only: NEVER DRAWN (every triangle NO_RENDER; planStructures fails fast on a drawn one), never a wall (WALK only); batch / far are the table\'s required fields and unused' },
  chamber: { batch: 'bore', far: 'drop', about: 'the room two tubes open into where their portals stand closer than a section (S6.d B4c-3 TWO PORTALS CLOSER THAN A SECTION ARE ONE MOUTH, kind 18): floor, walls, roof and the lintels over both openings, covered like the linings it joins — lamp-shaded bore look; far: its wall feet follow the far ring with the linings\' (a lining kind)' },
  'concrete-bed': { batch: 'lit', far: 'drop', body: true, about: 'A LOW FILL ON STEEP GROUND IS A CONCRETE BED (WAYS-G W-g, kind 20, world/fills.js through the fill builder): the low concrete edge holding a lane\'s formation where the ground falls away; daylight like a fill face; far: its foot follows the far ring; BODY: past structures.view_m the whole bed follows it (as embankment)' },
  'funicular-track': { batch: 'lit', far: 'ribbon', about: 'the funicular\'s OPEN STEEL TRACK (WAYS-G A3, kind 21, world/emit.js funicularTrack): rails, girder webs, cross beams and posts (steel, lit) on footings (concrete, lit); the sleeper strip is grating (the grate batch: sleepers across the track by the strip\'s own fall line, the gaps discarded — see-through, never blended); far: static with the car on its profile' },
  'funicular-stair': { batch: 'lit', far: 'ribbon', thin: true, about: 'the funicular\'s METAL SIDE STAIRS (WAYS-G A3, kind 22, world/emit.js funicularTrack): a grating sheet per side (the grate batch: open treads at the walkway riser DRAW.walkway_riser_m by world height, the riser gaps discarded, a nosing band at each tread\'s edge), its stringer, handrail and handrail posts (steel, lit); walkable through the one collider (the sheet WALK | STAIR); far: static with the track; THIN (rule D): a 0.7 m sheet, a 0.3 m stringer, a 5 cm handrail — drawn within DRAW.thin_view_m' },
};

// material -> colour (lit batch, sRGB hex, the S6.a palette) + tint (bore
// batch, multiplies boreTexture x lamp; > 1 brightens the dark rock texture
// toward the material, as roads3d AV_TINT does for gallery concrete).
// ONE ROW PER MATS ENTRY (fail fast on a missing one).
const lookRgb = (k) => { const [r, g, b] = WAYSLOOK.colour[k].base; return (r << 16) | (g << 8) | b; };
// a textured row (tex: <name>): its colour is the texture's albedo mean (tools/make-structtex.mjs)
const texRgb = (k) => parseInt(STRUCTTEX.textures[k].albedo_hex.slice(1), 16);
export const MAT_DRAW = {
  concrete: { color: 0xc6c2ba, tint: [1.55, 1.45, 1.25], about: 'cast concrete: pale grey; in a bore the gallery-concrete tint (roads3d AV_TINT)' },
  stone: { color: 0x8d867c, tint: [1.2, 1.15, 1.05], about: 'masonry: warm grey (baker-1 portal stone 0x8d867c)' },
  tarmac: { color: 0x4a4a4c, tint: [1.45, 1.45, 1.5], about: 'asphalt: dark grey; in a bore the baker-1 boreAsphaltTexture level (#3f3f45 over #1b1a1c)' },
  ballast: { color: 0x6f6a62, tint: [1.35, 1.3, 1.2], about: 'track ballast: grey-brown; in a bore the baker-1 floor grit level' },
  lining: { color: 0x777d84, tint: [1, 1, 1], batch: 'bore', about: 'bore lining: boreTexture as is — always the bore batch, whatever the kind' },
  steel: { color: 0x6d737a, tint: [1.6, 1.6, 1.7], about: 'steel railings / piers / rails: blue-grey' },
  gravel: { color: 0x9a948a, tint: [1.4, 1.35, 1.25], about: 'gravel walkway: light grey-brown' },
  timber: { color: 0x7a5a3a, tint: [1.3, 1.1, 0.9], about: 'timber: brown' },
  earth: { color: 0x6b5a45, tint: [1.2, 1.05, 0.9], about: 'earth fill face: brown' },
  grating: { color: 0x5f656b, tint: [1.5, 1.5, 1.6], batch: 'grate', about: 'open steel grating (WAYS-G A3): the funicular track\'s sleeper strip and the stair sheets, darker than steel railings; always the grate batch (alpha-tested pattern, structs2), never blended' },
  // (deck-top-is-its-way) a deck top carrying a paving / sett way: the colour IS
  // pipeline/wayslook.json colour.<kind>.base (one source with the ground's look)
  paving: { color: lookRgb('paving'), tint: [1.55, 1.45, 1.25], about: 'pavers (a deck top carrying a paving way): wayslook colour.paving.base' },
  sett: { color: lookRgb('sett'), tint: [1.2, 1.15, 1.05], about: 'sett / cobbles (a deck top carrying a sett way): wayslook colour.sett.base' },
  // (retaining-wall-stone, owner 2026-10-13: "retaining walls should always have a texture like this one")
  masonry: { color: texRgb('masonry'), tint: [1.2, 1.15, 1.05], tex: 'masonry', about: 'a retaining wall\'s face: irregular polygonal natural stone, grey to grey-green slabs, light joints — the lit batch samples client/materials/struct/masonry.jpg (tools/make-structtex.mjs, CC0 rock_wall_12) instead of the speckle; the colour IS its manifest albedo_hex, so the drawn mean is the photo\'s slab tone' },
  granite: { color: 0xcfcdc6, tint: [1.55, 1.45, 1.25], about: 'a retaining wall\'s coping: flat light-grey granite (the owner\'s photo), the speckle as any lit material' },
  // (bridge railings, owner 2026-10-16) a road railing's W-beam guard rail
  galvanized: { color: 0xa3a8ad, tint: [1.6, 1.6, 1.7], about: 'galvanized steel (a road railing\'s W-beam, emit.deck.railing.style.guard): light blue-grey; the railing\'s own parts take DRAW.railing\'s colours' },
  // (bridge-body-mat, owner 2026-10-16: "train: dark steel. road: concrete")
  steel_dark: { color: 0x3a3b3d, tint: [1.6, 1.6, 1.7], about: 'dark painted steel: a rail deck\'s girders, soffit and piers (emit.deck.mat.body.rail)' },
};

// shading / kit parameters (each with the reason it has that value)
export const DRAW = {
  detail_m: { v: 4, about: 'lit batch: box-mapped detail texture, metres per repeat (the concrete/stone grain reads at walking distance, no visible tiling at 30 m)' },
  detail_mean: { v: 0.92, about: 'sRGB level of the detail texture; lit colours are divided by its LINEAR value, so the batch keeps the S6.a palette brightness on average' },
  riser_m: { v: 0.17, about: 'STAIR triangles: a tread nosing every riser of HEIGHT (roads3d STAIR_RISER) — on a ramp, lines of equal height ARE the nosings; the emitted flight is a ramp (S6.a "stair treads drawn as ramps")' },
  bore_uv_m: { v: 2.5, about: 'bore batch texture metres per repeat, across (arc) and along (roads3d tunnelRun lining uv /2.5)' },
  // THE BORE LIGHT (D1-TUNC, owner 2026-10-11: "add light inside all tunnels, regularly placed lights at
  // the ceiling (invented)" — the ceiling lamps are an OWNER-LICENSED INVENTION, drawn only, no collider).
  // Every bore vertex carries a light E (rgb, linear, x the batch's texture / tint; boreLight): the
  // luminance max(portal daylight, lamp term x the bore's lamp colour), the hue blended by their shares. Units: E = 1 is today's old lamp
  // mean; the boreTexture is a dark damp rock (linear mean bore_tex_mean), so a lining at E 3 shows
  // ~30/255 and at E 12 ~105/255 through the frame's display transform (shadow tint + Neutral, whose
  // toe ate the old <= 1.35: tunnels drew BLACK). Per vertex at the lining's 2 m stations.
  lamp_m: { v: 25, about: 'ceiling lamp spacing along a bore (owner 2026-10-11 form: "every 25 m"), lamp k at s = L/2 + kL while s <= total; a fixture hangs at EXACTLY each station (boreLamps), so fixture and pool coincide by construction' },
  lamp_min_bore_m: { v: 50, about: 'only a bore LONGER than this gets ceiling lamps + pools (owner 2026-10-11 form: "for all: only when > 50m long"); a shorter one is lit by its portals alone (portal_*, bore_dim)' },
  lamp_fall_m: { v: 5.5, about: 'lamp pool falloff along s: g = 1 / (1 + (ds / fall)^2) (roads3d tunnelRun lit()); at 25 m spacing the gap midway keeps g 0.16, a pool spans ~11 m at half height' },
  lamp_dark: { v: 2.0, about: 'lamp term floor between the pools of a lit bore: dim, not black (a lining shows ~16-20/255, a ballast floor ~28/255 before the pools\' tails)' },
  lamp_gain: { v: 11, about: 'lamp pool gain, times the height factor (lamp_floor .. 1 from the floor to the crown): under a lamp the vault reaches E 13 (~110/255 lining), the floor E 9.7 (~115/255 ballast); midway the floor E 3.2' },
  lamp_floor: { v: 0.7, about: 'the pool\'s height factor ON THE FLOOR (1 at the crown, linear in up / crown): the vault next to the luminaire is lit hardest, the floor still clearly pooled' },
  lamp_max: { v: 14, about: 'lamp term cap' },
  lamp_flat: { v: 3.0, about: 'E (neutral) where a bore-batch triangle has no bore record to measure along (srcKind not bores: a flights portal cover, a decks gallery lining — 100 tris on 9cccf5be): about a lit bore\'s gap level' },
  portal_max: { v: 22, about: 'PORTAL DAYLIGHT (every bore): E (neutral) at each mouth (the first / last bore station of the record; outside them 0 m): the inside of a mouth reads near the daylit ground (~150/255 lining)' },
  portal_fall_m: { v: 8, about: 'portal daylight falloff inward from the nearer mouth: portal_max / (1 + (d / fall)^2) — half at 8 m, a fifth at 16 m, 4 % at 40 m' },
  bore_dim: { v: 2.5, about: 'E (neutral) floor of a bore WITHOUT lamps (<= lamp_min_bore_m): the middle of a short bore / underpass stays dim, never black (a lit bore\'s floor is lamp_dark x its lamp colour)' },
  lamp_look: { v: { major: 'sodium', minor: 'sodium', service: 'sodium', track: 'sodium', path: 'white', alpine: 'white', steps: 'white', 'rail-main': 'white', 'rail-siding': 'white', funicular: 'white' }, about: 'the lamp colour BY TUNNEL TYPE (owner 2026-10-11 form: road / service = sodium orange; rail, funicular, foot, passages = warm white): ways.json bores[k].cls (the path class the bore was cut for) -> lamp_rgb row; a road avalanche gallery is its road class (major); a track is a motorable road. ONE ROW PER CLASS: a class without a row throws (boreFrames), check-draw asserts every corridor class has one' },
  lamp_rgb: { v: { sodium: [1.0, 0.6, 0.25], white: [1.0, 0.92, 0.78] }, about: 'lamp colours (linear multipliers of the lamp term, fixture glow and pool alike): low-pressure-sodium orange; warm white ~3500 K' },
  fixture_len_m: { v: 1.2, about: 'ceiling luminaire (the invented lamp body, boreLamps): length along the bore axis tangent' },
  fixture_w_m: { v: 0.3, about: 'ceiling luminaire width across the axis' },
  fixture_h_m: { v: 0.12, about: 'ceiling luminaire height: its top at the station\'s ceiling (floor + section.top) - fixture_gap_m, its glowing underside fixture_h_m lower' },
  fixture_gap_m: { v: 0.03, about: 'luminaire top under the ceiling (an arch\'s crown is flat for +-0.6 m, a box roof flat across; check-draw --structures proves every fixture is under its lining)' },
  fixture_glow: { v: 3.0, about: 'the luminaire underside\'s radiance (linear, x lamp_rgb): above the display curve\'s white point, the fixture reads as the light source' },
  fixture_housing: { v: 0.25, about: 'the luminaire sides\' albedo (linear grey metal), lit like any bore part (bore_obj_k)' },
  bore_tex_mean: { v: 0.0151, about: 'the linear mean of client/src/roads3d.js boreTexture (its canvas reproduced: base #1b1a1c, speckle 30 +-15, joints, streaks): the 1x1 mip an UNTEXTURED bore part samples (bore uv = flatUv: per triangle (0,0) (K,0) (0,K), K = 1e7 -> the coarsest mip), so its vertex colour is divided by this; check-draw pins the boreTexture source it was measured on' },
  bore_obj_k: { v: 0.13, about: 'untextured bore parts (rails, sleepers, luminaire housing) show albedo x E x this: the bore\'s ballast floor (bore_tex_mean x tint 1.3) is ~0.13 x the surface ballast albedo, so a part keeps its daylight contrast to the floor it stands on' },
  rail_top_rgb: { v: 0xa9aeb3, about: 'bore rails: the running top (= client/src/ways2.js RAIL_TOP, the surface rail steel; check-draw gates equality)' },
  rail_foot_rgb: { v: 0x5b4637, about: 'bore rails: the weathered web / foot (= client/src/ways2.js RAIL_FOOT; check-draw gates equality); the head sides blend top -> foot 0.55 like railUnit' },
  rail_head_w_m: { v: 0.072, about: 'bore rails: the running top width (= ways2.js RAIL_PROFILE.head_w), the section a trapezoid widening to rail_foot_w_m at the floor: the surface rail\'s silhouette in the same 6 triangles per rail and segment' },
  rail_foot_w_m: { v: 0.15, about: 'bore rails: the foot width at the floor (= ways2.js RAIL_PROFILE.foot_w)' },
  tie_pitch_m: { v: 0.6, about: 'bore sleepers (waysdraw boreTies): pitch (= pipeline/wayslook.json ballast.sleeper_pitch_m; check-draw gates equality)' },
  tie_len_m: { v: 2.6, about: 'bore sleepers: length across (= wayslook ballast.sleeper_len_m, capped by doc.ballast.top_w_m - 2 shoulders like nearDetail)' },
  tie_w_m: { v: 0.26, about: 'bore sleepers: width along the track (= wayslook ballast.sleeper_w_m)' },
  tie_rgb: { v: [128, 124, 118], about: 'bore sleepers: concrete (= wayslook ballast.colour_sleeper, sRGB bytes), lit by the bore light (bore_obj_k), x a per-sleeper weathering 1 - 0.16 hash like ways2' },
  tie_h_m: { v: 0.2, about: 'bore sleepers: depth of the long faces under the top (= game/waysdraw.js DETAIL.depth_m, the ground sleeper box; no bottom, the ends inside the ballast shoulder)' },
  tie_view_m: { v: 150, about: 'bore sleepers are drawn within this many metres of the player only (= renderconfig.json ways.sleeper_view_m, the ground sleepers\' range; check-draw gates equality): a per-group block at the end of the bore batch, re-filled every tie_step_m; no far sleepers in bores' },
  tie_step_m: { v: 12, about: 'bore sleepers: re-selected after this much player movement (= renderconfig ways.detail_step_m)' },
  rail_lift_m: { v: 0.13, about: 'rail head over the bed: client/src/ways2.js RAIL_LIFT (the surface rails it draws)' },
  rail_w_m: { v: 0.07, about: 'rail head half-width (roads3d buildRails RW)' },
  rail_h_m: { v: 0.14, about: 'rail height (roads3d buildRails RH)' },
  rail_fork: { v: true, about: 'A TURNOUT ON A DECK IS DRAWN BY THE DECK\'S RAIL RUNS (S6.d B4c-5 P0d): a ways.json forks[] record (not a gap) any of whose legs\' node station is a structure station (flag != 0: deck / bore — the predicate game/waysdraw.js forkDraw names on_structure, which the surface draw leaves to the deck) is drawn here by the SAME kernel as the surface fork pass (waysdraw forkRails on legWalk frames of the deck stations): the diverging leg\'s blade (inner rail shifted off its stock rail) and its outer rail from where it peels off, plus the frog, as single rail lines (gauge 0 entries: one head line); the leg\'s stations inside the fork\'s own stretch are not drawn again as a plain rail pair, so no two drawn rail head lines overlap there. false: every leg a plain straight rail pair (the overlap; the red twin)' },
  rail_merge_m: { v: 0.01, about: 'THE RAIL STRIP MERGE (S6.d B3.1 economy): railRuns drops an interior station of a deck / bore rail run whose rail point (x, y + rail_lift_m, z) lies within this many metres of the straight line between the kept stations around it, on both sides the same bore flag; the first two and last two stations always stay (the end perps meet ways2.js\'s surface rails exactly). Drawn only (no collision, no felt ground: STR1 untouched); 12 triangles per dropped station' },
  group_chunks: { v: 4, about: 'draw group = this many x this many STR1 chunks (256 m) -> 1024 m, the buildings tile: worst resident draws on fc2789e9a7185bb2 at view_m 1360 are 69 at 1 (256 m), 38 at 2, 26 at 3, 19 at 4 (tris 78 k -> 92 k). check_budgets draw_calls on baker 2 is 255 of 264 before structures; 4 keeps baker 2 under the live 289 baseline (255 + 24 budget = 279)' },
  far_skin_m: { v: 0.10, about: 'FAR_RULE: a vertex at most this far ABOVE T0 is a ground contact, moved by the full signed T0 - G (the far-structs skin test; emit places feet on the skin). = renderconfig.structures.far_skin_m, where the bake reads the STG1 contacts band (check-focus gates equality)' },
  far_fade_m: { v: 1.0, about: 'FAR_RULE: above the skin the weight fades linearly from 1 (far_skin_m) to 0 (this height over T0), so a near-ground face is moved as a whole instead of torn: with a hard skin 6,682 drawn far:drop triangles (5,451 abutment) lowered one corner and kept another <= 1 m over T0 (check-feel "no near-ground face torn"); a vertex higher than this belongs to a face >= 0.9 m tall (a wall), which stretches, not tears. = renderconfig.structures.far_fade_m (check-focus gates equality)' },
  far_deep_m: { v: 25, about: 'FAR_RULE: a vertex deeper than this under T0 is never uncovered inside view_m (measured L2 max T0 - G 22 m) — skipped, it costs a sample per pass. = renderconfig.structures.far_deep_m (check-focus gates equality)' },
  far_body_ramp_m: { v: 200, about: 'FAR BODY RULE (farBodies / farBodyWeight): a vertex of a body record (a fill or cut whose connected records are all body kinds, thin kinds aside) moves with weight 1 once it is this many metres past structures.view_m, ramping linearly from its far_fade weight at view_m. Inside view_m the lane ribbon a fill carries is drawn at its true height, so the fill stretches down under it (the far_fade rule); past view_m (>= the farthest ribbon, check-focus) nothing static rides it, and a stretched fill was a wall from its crest to a far ring 45-65 m low in the L2->L3 window-min band (Muottas Muragl funicular fills at 1.6-1.8 km, bake b19dafed: brown 50 m "trees" the optic never showed). A ramp, not a step: weights vary with the vertex distance only, so a shared position moves the same for every copy and a record straddling view_m bends instead of tearing' },
  far_seam_pinned_max: { v: 18, about: 'FAR_RULE seam rule cap: drawn far:drop triangles that keep a PINNED seam vertex (at the position of a near-ground far:ribbon vertex, weight 0) next to a moved one — a mouth meeting a deck / stair lane (M5b, closed only when the ribbon kinds follow the far ring, S10). 18 on fc2789e9a7185bb2 (check-feel lists them); a bake whose seams grow past it is red, never silently accepted (26 on a +3 m STG1)' },
  far_step_m: { v: 8, about: 'FAR_RULE: the drape pass re-runs after the viewer moves this far (the envelope band is hundreds of metres wide) or when a terrain tile lands / leaves' },
  far_write_n: { v: 4096, about: 'FAR_RULE: drawn vertices written per upload step between clock reads (~0.3 ms): the densest group (structs2-5,5 on fc2789e9a7185bb2) writes 33,572, 2.6 ms in one piece, which put single frames at 4-5 ms ($A/tools/frametime.mjs, review R2-B5); the group is published (needsUpdate) after its last step, never half-written' },
  thin_view_m: { v: 500, about: 'THIN-KIND VIEW RULE (S6.d 2b rule D): a KIND_DRAW row with thin: true (parapet: railings <= 1.1 m tall, copings 0.3 m, 0.25 m thick) is drawn while its group is within this many metres of the viewer, not structures.view_m: 1.1 m at 500 m spans 2.1 px of 1080 lines at the 58.7 deg reference vFOV (main.js _REF_VFOV), at view_m 1360 0.8 px. The thin triangles sort LAST in their group batch; beyond thin_view_m the batch draws its head only (geometry drawRange): no extra draw call (as its own plan entries the worst resident draws were 25-26 > draw_budget 24 on the r3 W6 replay). The scope (focus) draws the whole group. On the W6 replay after rules A / B / E: worst resident 124698 -> 113923 tris, the densest head 41184 of 51910' },
  walkway_riser_m: { v: 0.18, about: 'the GRATE batch\'s stair treads (WAYS-G A3): a funicular-stair sheet draws one open tread per this much of world height — the walkway flights\' riser (world/params.json flights.walkway_riser_m; scripts/check-funicular.mjs gates equality). Lines of equal height ARE the treads on the sheet (as DRAW.riser_m on a raised flight)' },
  tread_gap: { v: 0.3, about: 'the GRATE batch\'s stair: the share of each riser period discarded (the open riser between two grating treads, seen through)' },
  grate_bore_light: { v: true, about: '(D1-TUNC R2) GRATING IN A BORE TAKES THE BORE LIGHT: a grate-batch triangle of a srcKind-bores STR1 record (the funicular strip / stair sheets D1-TUNW runs through bores 52 / 53) is drawn unlit and fog-free with vertex colour = grating albedo x boreLight(its bore frame) x bore_obj_k (the lining\'s light, by-type hue: warm white on a funicular), its stair byte 170 / 85 (structs2 GRATE_BORE_*), same program and discard pattern; grating outside bores (255 / 0) unchanged. false: the sunlit grate (the red twin; check-draw bores rows)' },
  sleeper_pitch_m: { v: 0.6, about: 'the GRATE batch\'s track strip: one steel sleeper per this many metres of plan arc along the strip\'s fall line (a funicular\'s sleeper spacing); a strip flatter than 3 % reads the world x instead' },
  sleeper_w_m: { v: 0.16, about: 'the GRATE batch\'s track strip: a sleeper\'s width along the track (the rest of each pitch discarded: the ground seen through)' },
  railing: { v: {
    merge_m: 0.02,
    style_of_mat: { galvanized: 'guard', steel: 'pipe', timber: 'wood' },
    styles: {
      guard: { pitch_m: 2.0, post_w_m: 0.12, post_d_m: 0.05, beam_h_m: 0.31, post_top: 0.96, post_rgb: 0x5c6066, rail_rgb: 0xa3a8ad },
      pipe: { pitch_m: 2.5, post_w_m: 0.06, rail_w_m: 0.05, rail_h_m: 0.05, mid_frac: 0.5, post_rgb: 0x3f4448, rail_rgb: 0x3f4448 },
      wood: { pitch_m: 2.5, post_w_m: 0.12, rail_w_m: 0.07, rail_h_m: 0.12, mid_frac: 0.5, post_lid: true, post_rgb: 0x6b4e33, rail_rgb: 0x7d5c3c },
    },
  }, about: 'OPEN RAILINGS (owner 2026-10-16: "no bridges should have solid side walls"; the photo: an RhB bridge\'s slim steel pipe railing, a timber footbridge\'s posts + two rails): a railing\'s collide-only quad (game/structures.js railQuad, the bake\'s emit.deck.railing) draws as posts + rails on the OUTER side of its felt face (client/src/railing.js railingGeom), lit batch, THIN tail (thin_view_m), no draw call of its own. Quads joined end to end into chains (same height + material), merged where the line stays within merge_m of straight. The style by the quad\'s material (style_of_mat: the bake picks it by way class). Posts: one at each chain end, evenly between at most pitch_m apart, post_w_m square (4 faces, wood a lid: 8 / 10 tris), its top at post_top x the railing height. guard: a W-beam (two faces kinked toward the way: edges post_d_m out of the felt face, crest on it), beam_h_m deep, top at the railing height, 4 tris per chain segment — the posts behind it (objects.js guard_rail: 0.12 m posts every 2 m, a 0.31 m beam). pipe / wood: two rails (top at the railing height, one at mid_frac x it) over the posts\' line, rail_w_m wide x rail_h_m deep, top + both sides (12 tris per segment for the pair). Colours sRGB (post_rgb / rail_rgb) through the lit batch\'s detail mean like MAT_DRAW.' },
  far_ms: { v: 350, about: 'FAR_RULE: at most one drape pass START per this many ms (roads3d _drape cadence)' },
  far_slice_ms: { v: 1.5, about: 'FAR_RULE: a pass spends about this much of one frame, counted from the start of StructDraw.update (residency included), then continues next frame: 256-sample blocks and far_write_n-vertex write steps between clock reads — a frame overshoots by at most one step (measured: APP_SURFACE §S7). A full pass at the densest spot is ~24k contact samples / 13-16 ms in node, never one frame' },
};
const P = (k) => DRAW[k].v;

// ------------------------------------------------------------ table lookups
export function kindRow(k, table = KIND_DRAW) {
  const name = KINDS[k];
  const r = name !== undefined ? table[name] : undefined;
  if (!r || !BATCHES.includes(r.batch) || !FAR.includes(r.far)) {
    throw new Error(`structs2: structure kind index ${k} (${name === undefined ? 'not in KINDS' : name}) has no ${r ? 'valid ' : ''}draw row — ${r ? `batch must be one of ${BATCHES}, far one of ${FAR}` : 'add it to KIND_DRAW'} (client/src/structplan.js)`);
  }
  return r;
}
export function matRow(m, table = MAT_DRAW) {
  const name = MATS[m];
  const r = name !== undefined ? table[name] : undefined;
  if (!r) {
    throw new Error(`structs2: material index ${m} (${name === undefined ? 'not in MATS' : name}) has no draw row — add it to MAT_DRAW (client/src/structplan.js)`);
  }
  return r;
}
// FAR_RULE weight of one vertex from its gap = T0 - y (the artifact): 1 =
// a ground contact (moved by the full T0 - G), 0 = static.
export function farWeight(gap) {
  if (!(gap <= P('far_deep_m'))) return 0;                       // deeper (or NaN): skipped
  if (gap >= -P('far_skin_m')) return 1;                         // on the skin or buried
  const f = P('far_fade_m');
  return gap > -f ? (f + gap) / (f - P('far_skin_m')) : 0;       // the fade band
}
// the SIGNED amount to lower a vertex of weight w, true ground t0, drawn
// ground G (terrain.groundDrawn): w * (t0 - G) — negative lifts it onto a
// drawn ground that lies above the true ground (review R2-B2); NaN G (no
// drawn node) or NaN t0 (no artifact) -> 0
export function farShift(w, t0, G) {
  return w > 0 && G === G && t0 === t0 ? w * (t0 - G) : 0;
}
// FAR_RULE for one vertex of a far:'drop' kind: gap = T0 - y (the artifact),
// t0 = y + gap, G = the drawn ground under it for this viewer -> the signed
// amount to lower the vertex (0 = keep, < 0 = lift).
export function farDrop(gap, t0, G) {
  return farShift(farWeight(gap), t0, G);
}
// the per-vertex weights the draw applies (structs2 build; the gate
// check-feel reads the same): the kind's far column, then farWeight, then
// the SEAM rule — a far:'drop' vertex at the position (1 cm grid) of a
// far:'ribbon' vertex keeps weight 0: the ribbon record is static, so the
// shared seam stays closed (the 6 of 222 seams no kind row can close: a
// mouth meeting a deck / stair). -> Float32Array(nVerts), 0 = static.
export function farContacts(str, gap, table = KIND_DRAW) {
  const W = new Float32Array(str.nVerts);
  if (!gap) return W;
  const V = str.verts;
  const ribbon = [];                                            // near-ground ribbon vertices
  for (let s = 0; s < str.kind.length; s++) {
    const drop = kindRow(str.kind[s], table).far === 'drop';
    for (let v = str.firstVert[s]; v < str.firstVert[s] + str.nVert[s]; v++) {
      if (drop) W[v] = farWeight(gap[v]);
      else if (farWeight(gap[v]) > 0) ribbon.push(v);           // only a near-ground ribbon vertex can meet a contact
    }
  }
  if (!ribbon.length) return W;
  // an open-addressing set of 1 cm positions (three Int32 per slot, no
  // strings): insert the ribbon vertices, probe every contact
  let cap = 1024;
  while (cap < ribbon.length * 2) cap *= 2;
  const K = new Int32Array(cap * 3), used = new Uint8Array(cap), mask = cap - 1;
  const cm = (v, c) => Math.round(V[v * 3 + c] * 100);
  const slot = (x, y, z) => {
    let h = Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791);
    for (let i = (h >>> 0) & mask; ; i = (i + 1) & mask) {
      if (!used[i] || (K[i * 3] === x && K[i * 3 + 1] === y && K[i * 3 + 2] === z)) return i;
    }
  };
  for (const v of ribbon) {
    const x = cm(v, 0), y = cm(v, 1), z = cm(v, 2), i = slot(x, y, z);
    used[i] = 1; K[i * 3] = x; K[i * 3 + 1] = y; K[i * 3 + 2] = z;
  }
  for (let v = 0; v < str.nVerts; v++) if (W[v] > 0 && used[slot(cm(v, 0), cm(v, 1), cm(v, 2))]) W[v] = 0;
  return W;
}

// FAR BODY RULE: the vertices of the records that move AS A WHOLE past
// structures.view_m (DRAW.far_body_ramp_m). A record is a body when its kind
// row says body: true AND every record it shares a position with (1 cm grid,
// transitively) is a body kind too — thin kinds aside, the naked eye draws
// them within thin_view_m < view_m only — and every vertex has a ground
// (gap not NaN). A fill touching a wall, a deck or an abutment keeps the
// stretch rule: moving it alone would part it from that static neighbour.
// -> Uint8Array(nVerts), 1 = a body vertex (all zero without a gap).
export function farBodies(str, gap, table = KIND_DRAW) {
  const B = new Uint8Array(str.nVerts);
  if (!gap) return B;
  const V = str.verts, ns = str.kind.length;
  const body = new Uint8Array(ns);
  let nb = 0;
  for (let s = 0; s < ns; s++) {
    const r = kindRow(str.kind[s], table);
    if (r.body && r.far === 'drop') { body[s] = 1; nb += str.nVert[s]; }
  }
  if (!nb) return B;
  const par = Int32Array.from({ length: ns }, (_, i) => i);
  const find = (i) => { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
  // open-addressing set of the body vertices' 1 cm positions -> record
  let cap = 1024;
  while (cap < nb * 2) cap *= 2;
  const K = new Int32Array(cap * 3), R = new Int32Array(cap).fill(-1), mask = cap - 1;
  const slot = (x, y, z) => {
    let h = Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791);
    for (let i = (h >>> 0) & mask; ; i = (i + 1) & mask) {
      if (R[i] < 0 || (K[i * 3] === x && K[i * 3 + 1] === y && K[i * 3 + 2] === z)) return i;
    }
  };
  const cm = (v, c) => Math.round(V[v * 3 + c] * 100);
  const mixed = new Uint8Array(ns);
  for (let s = 0; s < ns; s++) {
    if (!body[s]) continue;
    for (let v = str.firstVert[s]; v < str.firstVert[s] + str.nVert[s]; v++) {
      if (gap[v] !== gap[v]) mixed[s] = 1;
      const x = cm(v, 0), y = cm(v, 1), z = cm(v, 2), i = slot(x, y, z);
      if (R[i] < 0) { R[i] = s; K[i * 3] = x; K[i * 3 + 1] = y; K[i * 3 + 2] = z; } else { const a = find(R[i]), b = find(s); if (a !== b) par[a] = b; }
    }
  }
  for (let s = 0; s < ns; s++) {
    if (body[s]) continue;
    const r = kindRow(str.kind[s], table);
    if (r.thin || r.felt_only) continue;
    for (let v = str.firstVert[s]; v < str.firstVert[s] + str.nVert[s]; v++) {
      const i = slot(cm(v, 0), cm(v, 1), cm(v, 2));
      if (R[i] >= 0) mixed[R[i]] = 1;
    }
  }
  const bad = new Uint8Array(ns);
  for (let s = 0; s < ns; s++) if (body[s] && mixed[s]) bad[find(s)] = 1;
  for (let s = 0; s < ns; s++) if (body[s] && !bad[find(s)]) B.fill(1, str.firstVert[s], str.firstVert[s] + str.nVert[s]);
  return B;
}
// the weight the drape applies to a vertex: its far_fade weight w, raised to
// 1 over DRAW.far_body_ramp_m past viewM for a body vertex at distance d
// from the viewer
export function farBodyWeight(w, body, d, viewM) {
  if (!body || !(d > viewM)) return w;
  const r = Math.min(1, (d - viewM) / P('far_body_ramp_m'));
  return w + (1 - w) * r;
}

// STG1 — the structures GROUND artifact (proposed, STATE ORCHESTRATOR-MERGE):
//   'STG1' | u32 nVerts | i16 gap_cm[nVerts]
// gap_cm = round((T0(x, z) - y) * 100) per structures.bin vertex, clamped to
// +-32767; -32768 = no ground (outside the heightfield). -> Float32 gap (m,
// NaN = none), or throws on a vertex-count mismatch with the structures.
export function loadGround(buf, nVerts) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const magic = String.fromCharCode(u8[0], u8[1], u8[2], u8[3]);
  if (magic !== 'STG1') throw new Error(`structures ground: bad magic ${JSON.stringify(magic)}`);
  const n = dv.getUint32(4, true);
  if (n !== nVerts) throw new Error(`structures ground: ${n} vertices, structures.bin has ${nVerts}`);
  if (u8.byteLength < 8 + 2 * n) throw new Error(`structures ground: truncated (${u8.byteLength} B for ${n} vertices)`);
  const gap = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const c = dv.getInt16(8 + 2 * i, true);
    gap[i] = c === -32768 ? NaN : c / 100;
  }
  return gap;
}

// the batch of one triangle: the kind's row, unless its material forces one
export function batchOf(kind, mat) {
  const m = matRow(mat);
  return m.batch || kindRow(kind).batch;
}

// ------------------------------------------------------------------ rails
// The rails over DECK and BORE stations (ways.json flag 1, kind deck / bore):
// client/src/ways2.js draws the ballast ribbon + rails on SURFACE stations
// only ("the rails on a deck: S7") — here they continue over the structure,
// from the last surface station before it to the first after it, so the rails
// are one line through every bridge and tunnel (a bore segment drawn in the
// surface rail's look, lit by its bore frame: boreExtras below). Artifact fields only:
// ways[].cls / x / y / z / kind / flag, forks[] and the gauge of waysdraw
// railGauges (the ground rails' own resolution, S6.d B4c-5 A1).
// -> [{gauge, pts: [[x, y, z]...], bore: [0|1 per segment], run, i0, i1,
// fork?, leg?, role?}] — a pair entry draws run `run`'s stations i0..i1
// (merged) at `gauge` (waysdraw railGauges(doc)[run]); a gauge 0 entry is
// ONE head line (a fork's blade / outer rail / frog, run = its leg's run);
// the array carries .forks: [{k, kind, on_structure, drawn, why, lines}]
// (every fork record railRuns reads, the deck ones drawn here).
// (BRIDGE P1, owner 2026-10-16: "the train tracks should look the same on
// the bridge as off the bridge") A DOC WITH decks[] HAS ITS DECK RAILS DRAWN
// AS THE GROUND'S: client/src/ways2.js (deckway.js deckRails) draws the
// deck stretches' rails as railUnit instances on the way's look, from this
// function's deck: 'only' list. So by default (the structure draw) such a
// doc's runs hold BORE stations only (a deck neighbour joins as an end, as
// a surface one) and the deck turnouts' lines drop out unless one touches a
// bore; deck: 'only' -> the deck stations only (never a bore / drop
// neighbour: the bore run meets it at their shared station), the turnout
// lines without a bore segment, no bore extras, no open-track run. A doc
// without decks[] keeps every deck + bore station here (deck: 'only' -> []).
// opts: {fork (DRAW.rail_fork), worldRev (waysdraw forkDoc's), deck}
export function railRuns(doc, opts = {}) {
  const it = railSteps(doc, opts);
  let s;
  do s = it.next(); while (!s.done);
  return s.value;
}
// railRuns cut into tasks (main.js WaysDrawNet, the load: ~0.15–0.27 s as
// one task on a desktop): `pause` (sched.js slice) is awaited at every step
// of the ONE implementation below — the same output, built in the same order
export async function railRunsSliced(doc, opts, pause) {
  const it = railSteps(doc, opts);
  let s;
  while (!(s = it.next()).done) await pause();
  return s.value;
}
const RAIL_STEP_RUNS = 256;     // ways.json runs per step
const RAIL_STEP_TIES = 4096;    // bore sleepers per step
function* railSteps(doc, opts = {}) {
  const out = [];
  const K = (doc.codes && doc.codes.kind) || ['surface', 'deck', 'bore', 'drop'];
  const DROP = K.indexOf('drop'), BORE = K.indexOf('bore');
  // (S6.d B4c-5 A1) ONE GAUGE RESOLUTION: game/waysdraw.js railGauges (the
  // gauge forkDraw draws the ground rails at: run.gauge_m, else the track's
  // fork group's, else the class default) — a deck / bore stretch meets
  // its ground rails at one gauge
  if (typeof WD.railGauges !== 'function') throw new Error('structplan railRuns: game/waysdraw.js exports no railGauges (the one gauge resolution of the ground and structure rails, S6.d B4c-5 A1)');
  const GA = WD.railGauges(doc, { worldRev: opts.worldRev });
  yield;
  const FK = deckForks(doc, K, opts.fork !== undefined ? opts.fork : P('rail_fork'), GA);
  yield;
  const wayDecks = Array.isArray(doc.decks) && doc.decks.length > 0, deckOnly = opts.deck === 'only';
  // (WAYS-G A3) AN OPEN TRACK CARRIES ITS OWN RAILS: a run of an open-track
  // class (ways.json corridor.classes[cls].mode 'open') has its rails in its
  // STR1 funicular-track records over every surface and deck station
  // (world/emit.js funicularTrack); here only its BORE stretches (the
  // lamp-shaded bore batch), from the station before to the one after
  const CC = (doc.corridor && doc.corridor.classes) || {};
  const W = doc.ways || [];
  for (let r = 0; r < W.length; r++) {
    if (r % RAIL_STEP_RUNS === RAIL_STEP_RUNS - 1) yield;
    const e = W[r];
    const gauge = GA[r];
    if (!gauge || !Array.isArray(e.x) || e.x.length < 2) continue;
    const n = e.x.length;
    const openTrack = !!(CC[e.pcls] && CC[e.pcls].mode === 'open');
    if (deckOnly && (openTrack || !wayDecks)) continue;
    const on = openTrack || (wayDecks && !deckOnly) ? (i) => e.kind[i] === BORE
      : deckOnly ? (i) => e.flag[i] !== 0 && e.kind[i] !== DROP && e.kind[i] !== BORE
        : (i) => e.flag[i] !== 0 && e.kind[i] !== DROP;
    const end = deckOnly ? (k) => e.kind[k] !== DROP && e.kind[k] !== BORE : (k) => e.kind[k] !== DROP;
    const own = (k) => FK.own.has(r + ':' + k);
    let i = 0;
    while (i < n) {
      if (!on(i)) { i++; continue; }
      let j = i;
      while (j + 1 < n && on(j + 1)) j++;
      const a = i > 0 && end(i - 1) ? i - 1 : i;
      const b = j + 1 < n && end(j + 1) ? j + 1 : j;
      // the plain pair, split where a deck fork's own lines draw
      let all = [], seg = [], k0 = a;
      const flush = (k1) => { if (all.length >= 2) out.push({ ...railMerge(all, seg, gauge), run: r, i0: k0, i1: k1 }); all = []; seg = []; };
      for (let k = a; k <= b; k++) {
        if (own(k)) { flush(k - 1); k0 = k + 1; continue; }
        if (all.length) seg.push(e.kind[k - 1] === BORE || e.kind[k] === BORE ? 1 : 0);
        all.push([e.x[k], e.y[k] + P('rail_lift_m'), e.z[k]]);
      }
      flush(b);
      i = j + 1;
    }
  }
  for (const L of FK.lines) if (wayDecks ? deckOnly !== L.bore.some(Boolean) : !deckOnly) out.push(L);
  out.forks = FK.list;
  if (!deckOnly) yield* boreExtras(doc, out);
  return out;
}
// (D1-TUNC) THE TRACK AND THE LAMPS IN A BORE ride the rails list, so every
// reader of chunkPlan(str, railRuns(doc)) — the draw and the budget gates —
// charges them:
//   entry.bf  Int32Array per point: the bore frame (boreFrames index) a
//             bore segment's vertices are lit by (frameFinder; a point no
//             bore claims takes its neighbour's), -1 = none (lamp_flat)
//   .lamps    boreLamps: the ceiling luminaires
//   .ties     {t: Float64Array 8 per sleeper (waysdraw boreTies), f:
//             Int32Array frame per sleeper, stats}: the bore sleepers, drawn
//             within tie_view_m of the player only
//   .bores    the boreFrames they were measured on
function* boreExtras(doc, out) {
  yield;
  const frames = boreFrames(doc);
  const find = frameFinder(doc, frames);
  let nb = 0;
  for (const L of out) {
    if (!L.bore.some(Boolean)) continue;
    if (++nb % 64 === 0) yield;
    const bf = new Int32Array(L.pts.length).fill(-1);
    L.pts.forEach((p, q) => { bf[q] = find(L.run, p[0], p[1] - P('rail_lift_m'), p[2]); });
    for (let q = 1; q < bf.length; q++) if (bf[q] < 0) bf[q] = bf[q - 1];
    for (let q = bf.length - 2; q >= 0; q--) if (bf[q] < 0) bf[q] = bf[q + 1];
    L.bf = bf;
  }
  out.lamps = boreLamps(frames);
  yield;
  if (typeof WD.boreTies !== 'function') throw new Error('structplan railRuns: game/waysdraw.js exports no boreTies (the bore sleepers, D1-TUNC)');
  const T = WD.boreTies(doc, { sleeper_pitch_m: P('tie_pitch_m'), sleeper_len_m: P('tie_len_m') });
  yield;
  // one lookup per run station a sleeper's segment starts at (its midpoint)
  const n = T.ties.length / 8, tf = new Int32Array(n), memo = new Map();
  for (let i = 0; i < n; i++) {
    if (i % RAIL_STEP_TIES === RAIL_STEP_TIES - 1) yield;
    const r = T.ties[8 * i + 6], q = T.ties[8 * i + 7], key = r * 1e6 + q;
    let k = memo.get(key);
    if (k === undefined) {
      const e = doc.ways[r];
      k = find(r, (e.x[q] + e.x[q + 1]) / 2, (e.y[q] + e.y[q + 1]) / 2, (e.z[q] + e.z[q + 1]) / 2);
      memo.set(key, k);
    }
    tf[i] = k;
  }
  out.ties = { t: T.ties, f: tf, stats: T.stats };
  out.bores = frames;
}
// A TURNOUT ON A DECK IS DRAWN BY THE DECK'S RAIL RUNS (DRAW.rail_fork,
// S6.d B4c-5 P0d). The predicate is waysdraw forkDraw's on_structure one (a
// leg's node station has flag != 0); the geometry is its kernel: forkRails
// on legWalk frames whose stations are the ones railRuns draws (a structure
// stretch and the surface station on either side of it), the lane centre
// lifted by rail_lift_m and the normal of waysdraw runFrames. -> {own: Set
// 'run:i' (stations the fork's lines draw: the plain pair splits there),
// lines: [entry], list: [{k, kind, on_structure, drawn, why, lines}]}
function deckForks(doc, K, on, GA) {
  if (typeof on !== 'boolean') throw new Error(`structplan railRuns: DRAW.rail_fork ${JSON.stringify(on)} is not a boolean`);
  const res = { own: new Set(), lines: [], list: [] };
  const runs = doc.ways || [];
  if (!Array.isArray(doc.forks) || !doc.forks.length) return res;
  const DROP = K.indexOf('drop'), BORE = K.indexOf('bore');
  const isOn = (f) => [...f.through, ...f.legs].some((l) => runs[l.run] && runs[l.run].flag[l.i] !== 0);
  const deck = doc.forks.map((f, k) => (f.kind !== 'gap' && isOn(f) ? k : -1)).filter((k) => k >= 0);
  doc.forks.forEach((f, k) => res.list.push({ k, kind: f.kind, on_structure: isOn(f), drawn: false, why: isOn(f) ? '' : 'a surface fork: game/waysdraw.js forkDraw draws it', lines: 0 }));
  if (!on) { for (const k of deck) res.list[k].why = 'DRAW.rail_fork false: the legs drawn as plain rail pairs'; return res; }
  if (!deck.length) return res;
  for (const fn of ['forkRails', 'legWalk', 'railNet', 'runFrames', 'railPoint']) {
    if (typeof WD[fn] !== 'function') throw new Error(`structplan railRuns: game/waysdraw.js exports no ${fn} (a deck turnout, forks[${deck[0]}], is drawn by its kernel: S6.d B4c-5 P0d)`);
  }
  const isRail = runs.map((e) => renderOf(e).rails > 0);
  const net = WD.railNet(doc, isRail), F = WD.runFrames(doc), lift = P('rail_lift_m');
  // the stations railRuns draws: a structure stretch and its neighbours
  const drawn = runs.map((e, r) => {
    if (!isRail[r]) return null;
    const n = e.x.length, d = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      if (e.flag[i] === 0 || e.kind[i] === DROP) continue;
      d[i] = 1;
      if (i > 0 && e.kind[i - 1] !== DROP) d[i - 1] = 1;
      if (i + 1 < n && e.kind[i + 1] !== DROP) d[i + 1] = 1;
    }
    return d;
  });
  const secOf = (r, i) => !!(drawn[r] && drawn[r][i]);
  const frameOf = (r, i, w) => (w === 'c' ? [runs[r].x[i], runs[r].y[i] + lift, runs[r].z[i]] : [F[r].nx[i], F[r].nz[i]]);
  const walk = (l, sMax) => { const W = WD.legWalk(doc, net, l, sMax, secOf, frameOf); return W.length >= 2 ? W : null; };
  const asLeg = (W) => (W ? { c: W.map((q) => q.c), n: W.map((q) => q.n), s: W.map((q) => q.s) } : null);
  const boreAt = (ref) => runs[ref.run].kind[ref.i] === BORE;
  const line = (pts, run, extra, bore) => { if (pts.length >= 2) res.lines.push({ gauge: 0, pts, bore, run, ...extra }); };
  for (const k of deck) {
    const f = doc.forks[k], st = res.list[k];
    const g = GA[f.legs[0].run];
    const fr = [walk(f.through[0], f.clear_m), walk(f.through[1], f.clear_m), ...f.legs.map((l) => walk(l, l.clear_s))];
    const R = WD.forkRails(f, fr.map(asLeg), g);
    let all = !!(fr[0] && fr[1]);
    R.forEach((rr, j) => {
      const W = fr[2 + j], l = f.legs[j];
      if (!rr) { all = false; return; }
      const kOwn = Math.min(W.length - 1, Math.max(1, rr.blade.length, rr.peel.p ? rr.peel.k : W.length - 1));
      for (let q = 1; q < kOwn; q++) for (const ref of W[q].refs) res.own.add(ref.run + ':' + ref.i);
      if (!secOf(l.run, l.i - l.dir)) res.own.add(l.run + ':' + l.i);
      const bseg = (q0, q1) => { const b = []; for (let q = q0; q < q1; q++) b.push(boreAt(W[q].refs[0]) || boreAt(W[q + 1].refs[0]) ? 1 : 0); return b; };
      const P3 = (q, sd) => WD.railPoint(W[q].c, W[q].n, sd, g);
      const inner = [];
      for (let q = 0; q <= kOwn; q++) { const b = rr.blade[q]; inner.push(b && b.k === q ? b.p.slice() : P3(q, rr.inner)); }
      line(inner, l.run, { fork: k, leg: j, role: 'blade' }, bseg(0, kOwn));
      if (rr.peel.p) {
        const outer = [rr.peel.p.slice()];
        for (let q = rr.peel.k; q <= kOwn; q++) outer.push(P3(q, rr.outer));
        line(outer, l.run, { fork: k, leg: j, role: 'outer' }, [boreAt(W[Math.max(0, rr.peel.k - 1)].refs[0]) ? 1 : 0, ...bseg(rr.peel.k, kOwn)]);
      }
      if (rr.frog) {
        const h = rr.frog.len / 2, a = rr.frog.at, d = rr.frog.dir, q = W[Math.min(W.length - 1, rr.frog.k)].refs[0];
        line([[a[0] - d[0] * h, a[1], a[2] - d[1] * h], [a[0] + d[0] * h, a[1], a[2] + d[1] * h]], l.run, { fork: k, leg: j, role: 'frog' }, [boreAt(q) ? 1 : 0]);
      } else if (l.frog_s !== null) all = false;
    });
    st.lines = res.lines.filter((L) => L.fork === k).length;
    st.drawn = all;
    st.why = all ? 'drawn by the deck\'s rail runs (A TURNOUT ON A DECK IS DRAWN BY THE DECK\'S RAIL RUNS)' : 'a leg has no frame on the deck stations or its frog is not inside the walked window';
  }
  return res;
}
// THE RAIL STRIP MERGE (DRAW.rail_merge_m): greedy from each kept station,
// the farthest station whose chord keeps every station between within
// rail_merge_m (3D) and one bore flag; the first two / last two stay
function railMerge(all, seg, gauge) {
  const n = all.length, tol = P('rail_merge_m');
  if (n <= 4) return { gauge, pts: all, bore: seg };
  const dist = (p, a, b) => {
    const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2], L2 = dx * dx + dy * dy + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy + (p[2] - a[2]) * dz) / L2)) : 0;
    return Math.hypot(a[0] + dx * t - p[0], a[1] + dy * t - p[1], a[2] + dz * t - p[2]);
  };
  const keep = [0, 1];
  let i = 1;
  while (i < n - 2) {
    let j = i + 1;
    for (let c = i + 2; c <= n - 2; c++) {
      let ok = seg[c - 1] === seg[i];
      for (let k = i + 1; k < c && ok; k++) ok = dist(all[k], all[i], all[c]) <= tol;
      if (!ok) break;
      j = c;
    }
    keep.push(j);
    i = j;
  }
  keep.push(n - 1);
  return { gauge, pts: keep.map((k) => all[k]), bore: keep.slice(0, -1).map((k) => seg[k]) };
}
// triangles one rail run adds: 2 rails x 3 faces x 2 tris per segment
export const RAIL_TRIS_PER_SEG = 12;

// -------------------------------------------------------------- bore frames
// Per ways.json bore: its stations (x, z, floor), the arc length s, the
// crown height and the lamp stations — what the bore batch measures a vertex
// against (along s, across = lateral + height over the floor, up / crown).
// (D1-TUNC) + the light it is lit by: k, cls, look / rgb (DRAW.lamp_look:
// the lamp colour by tunnel type), lit (total > lamp_min_bore_m: ceiling
// lamps), m0 / m1 (s of the first / last bore station: the mouths the
// portal daylight is measured from), top (ceiling over the floor per
// station, section.top else the crown), half, path.
export function boreFrames(doc) {
  const K = (doc.codes && doc.codes.kind) || ['surface', 'deck', 'bore', 'drop'];
  const BORE = K.indexOf('bore');
  return (doc.bores || []).map((b, k) => {
    if (!Array.isArray(b.x) || b.x.length < 2) return null;
    const n = b.x.length, s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(b.x[i] - b.x[i - 1], b.z[i] - b.z[i - 1]);
    const prof = b.section && b.section.prof;
    const crown = (b.section && b.section.crown) || (prof && prof.length ? Math.max(...prof.map((p) => p[1])) : 0) || 1;
    const look = lampLook(b.cls, k);
    const top = b.section && Array.isArray(b.section.top) && b.section.top.length === n ? b.section.top : new Array(n).fill(crown);
    let i0 = 0, i1 = n - 1;
    if (Array.isArray(b.kind) && b.kind.length === n && b.kind.includes(BORE)) { i0 = b.kind.indexOf(BORE); i1 = b.kind.lastIndexOf(BORE); }
    const total = s[n - 1];
    return { k, x: b.x, z: b.z, floor: b.floor, s, n, total, crown, cls: b.cls, look, rgb: P('lamp_rgb')[look],
      lit: total > P('lamp_min_bore_m'), m0: s[i0], m1: s[i1], top, half: (b.section && b.section.half) || 1.5, path: b.path };
  });
}
// the lamp colour row of a bore class (DRAW.lamp_look) — fail fast
export function lampLook(cls, k = '?') {
  const L = P('lamp_look')[cls];
  if (!L || !P('lamp_rgb')[L]) throw new Error(`structplan: bore ${k} class ${JSON.stringify(cls)} has no lamp colour row — add it to DRAW.lamp_look (client/src/structplan.js)`);
  return L;
}
// (x, y, z) against a bore frame -> {s, lat, up, d}: the nearest segment in
// plan (d = the plan distance to it)
export function boreCoord(f, x, y, z) {
  let best = Infinity, bs = 0, bl = 0, bf = 0;
  for (let i = 0; i + 1 < f.n; i++) {
    const ax = f.x[i], az = f.z[i], dx = f.x[i + 1] - ax, dz = f.z[i + 1] - az;
    const L2 = dx * dx + dz * dz || 1e-9;
    const t = Math.min(1, Math.max(0, ((x - ax) * dx + (z - az) * dz) / L2));
    const px = ax + dx * t, pz = az + dz * t;
    const d = Math.hypot(x - px, z - pz);
    if (d < best) {
      best = d;
      bs = f.s[i] + (f.s[i + 1] - f.s[i]) * t;
      bl = ((x - ax) * dz - (z - az) * dx) / Math.sqrt(L2);
      bf = f.floor[i] + (f.floor[i + 1] - f.floor[i]) * t;
    }
  }
  return { s: bs, lat: bl, up: y - bf, d: best };
}
// the lamp stations of a bore: s = L/2 + kL (k = 0 .. nL - 1, nL =
// floor((total - L/2) / L) + 1) on a LIT bore (total > lamp_min_bore_m),
// none on a shorter one — the pools (lampShade) and the fixtures
// (boreLamps) both read this
export function lampStations(f) {
  if (!f || !f.lit) return [];
  const L = P('lamp_m'), nL = Math.max(1, Math.floor((f.total - L * 0.5) / L) + 1);
  return Array.from({ length: nL }, (_, k) => L * 0.5 + k * L);
}
// the lamp term (scalar E, x the bore's lamp colour in boreLight) at arc s,
// height up over the floor: lamp_dark + lamp_gain x the nearest station's
// pool g x the height factor, capped; 0 on a bore without lamps
export function lampShade(f, s, up) {
  if (!f.lit) return 0;
  const L = P('lamp_m'), nL = Math.max(1, Math.floor((f.total - L * 0.5) / L) + 1);
  const k = Math.min(nL - 1, Math.max(0, Math.round((s - L * 0.5) / L)));
  const ds = (s - (L * 0.5 + k * L)) / P('lamp_fall_m');
  const g = 1 / (1 + ds * ds);
  const h = Math.min(1, Math.max(0, up / f.crown)), lf = P('lamp_floor');
  return Math.min(P('lamp_max'), P('lamp_dark') + P('lamp_gain') * g * (lf + (1 - lf) * h));
}
// the portal daylight (scalar E, neutral) at arc s: portal_max at either
// mouth (and outside it), falling off inward from the nearer one
export function portalShade(f, s) {
  const d = Math.max(0, Math.min(s - f.m0, f.m1 - s)) / P('portal_fall_m');
  return P('portal_max') / (1 + d * d);
}
// THE BORE LIGHT at arc s, height up -> out [r, g, b] (linear E). A lit
// bore: the LUMINANCE is max(portal daylight p, lamp term l x lum(lamp
// colour)), the hue the two lights' blend by their shares — a per-channel max
// of neutral daylight and sodium drew a magenta band where they cross (R from
// the lamp, B from the portal: tools/section.py on bore 22). p alone ->
// neutral p; the lamp alone -> l x lamp colour. Without lamps max(portal,
// bore_dim), neutral.
const lumOf = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
export function boreLight(f, s, up, out = [0, 0, 0]) {
  const p = portalShade(f, s);
  if (f.lit) {
    const c = f.rgb, lc = lumOf(c), lL = lampShade(f, s, up) * lc, m = Math.max(p, lL), w = p + lL;
    const a = w > 0 ? (m * p) / w : 0, b = w > 0 ? (m * lL) / (w * lc) : 0;   // neutral part, lamp-colour part
    out[0] = a + b * c[0]; out[1] = a + b * c[1]; out[2] = a + b * c[2];
  } else {
    out[0] = out[1] = out[2] = Math.max(p, P('bore_dim'));
  }
  return out;
}
// the point of a bore frame at arc s -> {i (segment), t, x, z, floor, top, tx, tz}
export function boreAt(f, s) {
  let i = 0;
  while (i + 2 < f.n && f.s[i + 1] < s) i++;
  const L = f.s[i + 1] - f.s[i] || 1e-9, t = Math.max(0, Math.min(1, (s - f.s[i]) / L));
  const dx = f.x[i + 1] - f.x[i], dz = f.z[i + 1] - f.z[i], l = Math.hypot(dx, dz) || 1;
  return { i, t, x: f.x[i] + dx * t, z: f.z[i] + dz * t, floor: f.floor[i] + (f.floor[i + 1] - f.floor[i]) * t,
    top: f.top[i] + (f.top[i + 1] - f.top[i]) * t, tx: dx / l, tz: dz / l };
}
// THE CEILING LAMPS (owner-licensed invention 2026-10-11): one luminaire at
// EXACTLY every lamp station of every lit bore, on the axis, along its
// tangent, its top fixture_gap_m under the station's ceiling (floor +
// section.top), its glowing underside fixture_h_m lower — drawn only (no
// STR1 record, no collider), in the bore batch (FIXTURE_TRIS each),
// pitched with the ceiling along the bore (dy: its rise per metre along +t,
// the ceiling's over the fixture's length — a level body under a 6 % grade
// cut the lining by 4 cm at its downhill end).
// -> [{k (bore), j (station), s, x, z, y0 (top), y1 (underside) at the
// centre, dy, tx, tz, rgb}]
export function boreLamps(frames) {
  const out = [];
  const hl = P('fixture_len_m') / 2;
  frames.forEach((f) => {
    if (!f) return;
    lampStations(f).forEach((s, j) => {
      const a = boreAt(f, s), y0 = a.floor + a.top - P('fixture_gap_m');
      const c0 = boreAt(f, Math.max(0, s - hl)), c1 = boreAt(f, Math.min(f.total, s + hl));
      const ds = Math.min(f.total, s + hl) - Math.max(0, s - hl);
      const dy = ds > 0 ? (c1.floor + c1.top - c0.floor - c0.top) / ds : 0;
      out.push({ k: f.k, j, s, x: a.x, z: a.z, y0, y1: y0 - P('fixture_h_m'), dy, tx: a.tx, tz: a.tz, rgb: f.rgb });
    });
  });
  return out;
}
// a luminaire: underside (2, the glow), two long sides (4), two ends (4)
export const FIXTURE_TRIS = 10;
// a bore sleeper: its top (2) and its two long faces (4) — the ends are
// within the ballast shoulder, the bottom buried
export const TIE_TRIS = 6;
// the bore frame a point of run r lies in: the bores cut for the run's
// path first (nearest in plan), else any bore within half + 4 m -> index or -1
function frameFinder(doc, frames) {
  const byPath = new Map();
  frames.forEach((f, k) => { if (!f) return; if (!byPath.has(f.path)) byPath.set(f.path, []); byPath.get(f.path).push(k); });
  const all = frames.map((f, k) => (f ? k : -1)).filter((k) => k >= 0);
  return (r, x, y, z) => {
    const e = doc.ways[r], own = e ? byPath.get(e.path) : null;
    let best = -1, bd = Infinity;
    for (const k of own && own.length ? own : all) {
      const d = boreCoord(frames[k], x, y, z).d;
      if (d < bd && (own && own.length ? true : d <= frames[k].half + 4)) { bd = d; best = k; }
    }
    return best;
  };
}

// ------------------------------------------------------------- chunk plan
// Which chunk every drawn triangle lands in, its batch, and each chunk's
// bounding sphere and cost — the draw builds from it, the gate sums it.
// Structures keep their STR1 chunk (one contiguous triangle range); a rail
// segment joins the chunk of its midpoint on the same grid.
export function chunkKey(cx, cz) { return cx + ',' + cz; }
export function chunkPlan(str, rails = [], group = P('group_chunks')) {
  const plan = new Map();
  const C = str.chunk > 0 ? str.chunk : 0;
  // (cx, cz) = the DRAW GROUP: group x group STR1 chunks (DRAW.group_chunks)
  const get = (sx, sz) => {
    const cx = Math.floor(sx / group), cz = Math.floor(sz / group);
    const k = chunkKey(cx, cz);
    let p = plan.get(k);
    if (!p) plan.set(k, p = { cx, cz, tris: Object.fromEntries(BATCHES.map((q) => [q, 0])), head: Object.fromEntries(BATCHES.map((q) => [q, 0])), bb: [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity], strs: [], rails: [], lamps: [], ties: [], railings: [] });
    return p;
  };
  const grow = (p, x, y, z) => {
    const b = p.bb;
    if (x < b[0]) b[0] = x; if (y < b[1]) b[1] = y; if (z < b[2]) b[2] = z;
    if (x > b[3]) b[3] = x; if (y > b[4]) b[4] = y; if (z > b[5]) b[5] = z;
  };
  const { verts, idx, mat, tflags } = str;
  for (const ch of str.chunks) {
    const p = get(ch.cx, ch.cz);
    p.strs.push(ch);
    for (let s = ch.first; s < ch.first + ch.count; s++) {
      const kind = str.kind[s];
      const row = kindRow(kind), thin = !!row.thin;   // fail fast per record
      for (let t = str.firstTri[s]; t < str.firstTri[s] + str.nTri[s]; t++) {
        // a felt-only kind (skin-copy) is never drawn: a drawn triangle of it fails fast
        if (row.felt_only && !(tflags[t] & TF.NO_RENDER)) throw new Error(`structs2: structure ${s} of the felt-only kind ${KINDS[kind]} has a drawn triangle ${t} (tflags ${tflags[t]}): THE FELT SKIN PAST THE UNION IS A RECORD OF THE DRAWN SKIN, never drawn (client/src/structplan.js KIND_DRAW)`);
        if (tflags[t] & TF.NO_RENDER) continue;
        const bt = batchOf(kind, mat[t]);
        p.tris[bt]++;
        if (!thin) p.head[bt]++;
        for (let c = 0; c < 3; c++) {
          const v = idx[t * 3 + c] * 3;
          grow(p, verts[v], verts[v + 1], verts[v + 2]);
        }
      }
    }
  }
  // (bridge railings) a group's open railings: chains of its records'
  // railing quads, posts + rails in the lit batch's thin tail (railing.js)
  const RD = P('railing');
  for (const p of plan.values()) {
    const qs = [];
    for (const ch of p.strs) qs.push(...railingsOf(str, ch.first, ch.first + ch.count).rails);
    p.railings = qs.length ? railingChains(qs, RD, MATS) : [];
    for (const c of p.railings) {
      const n = railingGeom(c, RD);
      c.n = n;
      p.tris.lit += n;
      for (const q of c.pts) { grow(p, q[0] - 0.3, q[1], q[2] - 0.3); grow(p, q[0] + 0.3, q[1] + c.h, q[2] + 0.3); }
    }
  }
  for (const r of rails) {
    for (let i = 0; i + 1 < r.pts.length; i++) {
      const a = r.pts[i], b = r.pts[i + 1];
      const mx = (a[0] + b[0]) / 2, mz = (a[2] + b[2]) / 2;
      const p = C > 0 ? get(Math.floor(mx / C), Math.floor(mz / C)) : get(0, 0);
      p.rails.push([r, i]);
      p.tris[r.bore[i] ? 'bore' : 'lit'] += RAIL_TRIS_PER_SEG;
      p.head[r.bore[i] ? 'bore' : 'lit'] += RAIL_TRIS_PER_SEG;
      grow(p, a[0], a[1], a[2]); grow(p, b[0], b[1], b[2]);
    }
  }
  // (D1-TUNC) the bore extras join the group of their position — or, if that
  // group has no bore batch, the nearest neighbouring group that has one (a
  // luminaire or a sleeper never opens a draw call of its own)
  const home = (x, z) => {
    const gx = C > 0 ? Math.floor(Math.floor(x / C) / group) : 0, gz = C > 0 ? Math.floor(Math.floor(z / C) / group) : 0;
    const own = plan.get(chunkKey(gx, gz));
    if (own && own.tris.bore > 0) return own;
    let best = null, bd = Infinity;
    for (let a = -1; a <= 1; a++) for (let c = -1; c <= 1; c++) {
      const q = plan.get(chunkKey(gx + a, gz + c));
      if (!q || !(q.tris.bore > 0)) continue;
      const d = Math.hypot((q.bb[0] + q.bb[3]) / 2 - x, (q.bb[2] + q.bb[5]) / 2 - z);
      if (d < bd) { bd = d; best = q; }
    }
    return best || (C > 0 ? get(Math.floor(x / C), Math.floor(z / C)) : get(0, 0));
  };
  const lamps = rails.lamps || [];
  const homes = lamps.map((L) => home(L.x, L.z));
  lamps.forEach((L, i) => {
    const p = homes[i], h = P('fixture_len_m') / 2;
    p.lamps.push(L);
    p.tris.bore += FIXTURE_TRIS; p.head.bore += FIXTURE_TRIS;
    const e = Math.abs(L.dy) * h;
    grow(p, L.x - L.tx * h, L.y1 - e, L.z - L.tz * h); grow(p, L.x + L.tx * h, L.y0 + e, L.z + L.tz * h);
  });
  const T = rails.ties || null, nT = T ? T.t.length / 8 : 0;
  const tieHomes = [];
  for (let i = 0; i < nT; i++) tieHomes.push(home(T.t[8 * i], T.t[8 * i + 2]));
  for (let i = 0; i < nT; i++) {
    const p = tieHomes[i], o = 8 * i, h = T.t[o + 5] / 2;
    p.ties.push(i);
    grow(p, T.t[o] - T.t[o + 3] * h, T.t[o + 1] - P('tie_h_m'), T.t[o + 2] - T.t[o + 4] * h);
    grow(p, T.t[o] + T.t[o + 3] * h, T.t[o + 1], T.t[o + 2] + T.t[o + 4] * h);
  }
  for (const p of plan.values()) {
    const b = p.bb;
    p.x = (b[0] + b[3]) / 2; p.y = (b[1] + b[4]) / 2; p.z = (b[2] + b[5]) / 2;
    p.r = Math.hypot(b[3] - b[0], b[4] - b[1], b[5] - b[2]) / 2;
    p.draws = BATCHES.filter((k) => p.tris[k] > 0 || (k === 'bore' && p.ties.length > 0)).length;   // a sleeper-only bore batch is a draw too
    p.n = BATCHES.reduce((t, q) => t + p.tris[q], 0);
    p.nHead = BATCHES.reduce((t, q) => t + p.head[q], 0);        // the thin kinds' tail left out
    // the near sleepers: NOT in n (never drawn past tie_view_m, so never in
    // a focus group); tieXZ for the near count, tieCap = the most a viewer
    // anywhere can have within tie_view_m (the draw's block size)
    if (p.ties.length) {
      p.tieXZ = new Float64Array(p.ties.length * 2);
      p.ties.forEach((i, j) => { p.tieXZ[2 * j] = T.t[8 * i]; p.tieXZ[2 * j + 1] = T.t[8 * i + 2]; });
      tieIndex(p);
      p.tieCap = tieCapOf(p, P('tie_view_m'));
    } else { p.tieXZ = null; p.tieCap = 0; }
  }
  return plan;
}
// a group's sleepers bucketed on a TIE_CELL grid (the near queries)
const TIE_CELL = 32;
function tieIndex(p) {
  const X = p.tieXZ, cells = new Map();
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (let j = 0; j < X.length; j += 2) {
    x0 = Math.min(x0, X[j]); x1 = Math.max(x1, X[j]); z0 = Math.min(z0, X[j + 1]); z1 = Math.max(z1, X[j + 1]);
    const k = Math.floor(X[j] / TIE_CELL) + ',' + Math.floor(X[j + 1] / TIE_CELL);
    let L = cells.get(k);
    if (!L) cells.set(k, L = []);
    L.push(j >> 1);
  }
  p.tieCells = cells; p.tieBB = [x0, z0, x1, z1];
}
// the sleepers of a group within r of (x, z) -> count, or (out) their
// indices into p.ties with their squared distances appended to out / d2
export function tiesNear(p, x, z, r, out = null, d2 = null) {
  const X = p.tieXZ;
  if (!X) return 0;
  const B = p.tieBB;
  if (x < B[0] - r || x > B[2] + r || z < B[1] - r || z > B[3] + r) return 0;
  let n = 0;
  const r2 = r * r;
  const i0 = Math.floor((x - r) / TIE_CELL), i1 = Math.floor((x + r) / TIE_CELL);
  const j0 = Math.floor((z - r) / TIE_CELL), j1 = Math.floor((z + r) / TIE_CELL);
  for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) {
    const L = p.tieCells.get(i + ',' + j);
    if (!L) continue;
    for (const q of L) {
      const dx = X[2 * q] - x, dz = X[2 * q + 1] - z, dd = dx * dx + dz * dz;
      if (dd <= r2) { n++; if (out) { out.push(q); d2.push(dd); } }
    }
  }
  return n;
}
// an UPPER BOUND of the sleepers any viewer can have within r (the draw's
// block size; the budget counts the true near set, tiesNear): on an 8 m
// cell grid, the most sleepers in any square of (2h + 1) cells, h =
// ceil(r / 8) + 1 — the square around a viewer's cell holds its disc —
// by a summed-area table, capped by the total
function tieCapOf(p, r) {
  const n = p.ties.length, B = p.tieBB, c = 8, h = Math.ceil(r / c) + 1, X = p.tieXZ;
  const W = Math.floor((B[2] - B[0]) / c) + 1, H = Math.floor((B[3] - B[1]) / c) + 1;
  const S = new Int32Array((W + 1) * (H + 1));
  for (let j = 0; j < X.length; j += 2) S[(Math.floor((X[j + 1] - B[1]) / c) + 1) * (W + 1) + Math.floor((X[j] - B[0]) / c) + 1]++;
  for (let z = 1; z <= H; z++) for (let x = 1; x <= W; x++) S[z * (W + 1) + x] += S[(z - 1) * (W + 1) + x] + S[z * (W + 1) + x - 1] - S[(z - 1) * (W + 1) + x - 1];
  const at = (x, z) => S[Math.max(0, Math.min(H, z)) * (W + 1) + Math.max(0, Math.min(W, x))];
  let best = 0;
  for (let z = 0; z < H; z++) for (let x = 0; x < W; x++) {
    const x0 = x - h, x1 = x + h + 1, z0 = z - h, z1 = z + h + 1;
    const s = at(x1, z1) - at(x0, z1) - at(x1, z0) + at(x0, z0);
    if (s > best) best = s;
  }
  return Math.min(n, best);
}

// Naked-eye residency (StructDraw.update, the same test): a chunk is drawn
// while its bounding sphere's near edge is within view_m of the viewer.
export function chunkShown(p, x, z, viewM) {
  return Math.hypot(p.x - x, p.z - z) - p.r < viewM;
}
// THE THIN-KIND VIEW RULE (DRAW.thin_view_m): a KIND_DRAW row with thin:
// true is a group batch's TAIL, drawn while the group is within
// min(thin_view_m, viewM); beyond it the batch draws its head (drawRange).
export function isThin(kind, table = KIND_DRAW) { return !!kindRow(kind, table).thin; }
export function thinShown(p, x, z, viewM, thinM = P('thin_view_m')) {
  return chunkShown(p, x, z, Math.min(thinM, viewM));
}
// the naked-eye triangles a group costs at (x, z): all, its head, or none;
// + (D1-TUNC) its bore sleepers within tieM of (x, z) (tieM given)
export function residentTris(p, x, z, viewM, thinM = P('thin_view_m'), tieM = 0) {
  if (!chunkShown(p, x, z, viewM)) return 0;
  return (thinShown(p, x, z, viewM, thinM) ? p.n : p.nHead) + (tieM > 0 && p.tieXZ ? TIE_TRIS * tiesNear(p, x, z, tieM) : 0);
}
// The residency pass (StructDraw.update): per chunk {x, z, r, n, draws} ->
// 0 hidden / 1 drawn (layer 0) / 2 focus-only (focus.layer). Beyond view_m a
// chunk the scope wedge hits within focus.structures.range_m is admitted
// nearest-to-APEX while the running tris <= tri_budget and draws <=
// draw_budget (the wedge budget is a hard cap, never exceeded); an inactive
// or tier-off focus admits nothing.
export function residency(chunks, x, z, viewM, focus, out = new Int8Array(chunks.length)) {
  const fc = (focus && focus.active && focus.structures) || null;
  const cand = [];
  for (let i = 0; i < chunks.length; i++) {
    const u = chunks[i];
    if (chunkShown(u, x, z, viewM)) { out[i] = 1; continue; }
    out[i] = 0;
    if (fc && focus.hit(u.x, u.z, u.r, fc.range_m)) cand.push(i);
  }
  if (cand.length) {
    const da = (i) => Math.hypot(chunks[i].x - focus.ox, chunks[i].z - focus.oz);
    cand.sort((a, b) => da(a) - da(b));
    let tris = 0, draws = 0;
    for (const i of cand) {
      const u = chunks[i];
      if (tris + u.n <= fc.tri_budget && draws + u.draws <= fc.draw_budget) {
        tris += u.n; draws += u.draws; out[i] = 2;
      }
    }
  }
  return out;
}

// worst resident set over viewer positions on a `step` grid (+ step/√2 of
// slack, so no real position can beat it) — upper bound, no frustum culling;
// a group costs its head beyond thin_view_m (residentTris) and its bore
// sleepers within tie_view_m (+ slack)
export function worstResident(plan, viewM, step = 32) {
  const P2 = [...plan.values()];
  if (!P2.length) return { tris: 0, draws: 0, at: null };
  let X = 0, Z = 0;
  for (const p of P2) { X = Math.max(X, p.x + p.r); Z = Math.max(Z, p.z + p.r); }
  const slack = step * Math.SQRT1_2;
  let tris = 0, draws = 0, at = null;
  for (let z = 0; z <= Z; z += step) {
    for (let x = 0; x <= X; x += step) {
      let t = 0, d = 0;
      for (const p of P2) {
        if (!chunkShown(p, x, z, viewM + slack)) continue;
        t += residentTris(p, x, z, viewM + slack, P('thin_view_m') + slack, P('tie_view_m') + slack); d += p.draws;
      }
      if (t > tris) { tris = t; at = [x, z]; }
      if (d > draws) draws = d;
    }
  }
  return { tris, draws, at };
}

export { KINDS, MATS, TF, SRC };
